import React, { useEffect, useState, useCallback } from 'react';
import PropTypes, { bool } from 'prop-types';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import Button from '../../../components/Button/CustomButton';
import Typography from '../../../components/Typography/CustomTypography';
import SnackBar from '../../../components/Snackbar/Snackbar';
import styles from '../../../assets/jss/pages/surveyMangementStyle';
import CleanableInput from '../../../components/Input/CleanableInput';
import Validation from '../../../common/Validation';
import Loader from '../../../components/Loader/Loader';

const useStyles = makeStyles(styles);

const SurveryManagement = (props) => {
    const {
        fetchClearGetSurvey,
        fetchGetSurvey,
        fetchClearUpdateSurvey,
        fetchUpdateSurvey,
        surveyData,
        isFetchingUpdate,
        updateSuccess,
        isLoading,
        eventInfo,
    } = props;
    const classes = useStyles();
    const urlParams = useParams();
    const intl = useIntl();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);
    const [params, setParams] = useState({});
    const [validateResults, setValidateResults] = useState({});

    useEffect(() => {
        fetchGetSurvey(urlParams.shortName);
        return () => {
            fetchClearGetSurvey();
            fetchClearUpdateSurvey();
        };
    }, []);

    useEffect(() => {
        if (!_.isEmpty(surveyData)) {
            setParams({
                ...params,
                survey_link_viewer: surveyData.survey_link_viewer,
                survey_link_organizer: surveyData.survey_link_organizer,
                survey_link_viewer_en: surveyData.survey_link_viewer_en,
                survey_link_organizer_en: surveyData.survey_link_organizer_en,
            });
        }
    }, [surveyData]);

    useEffect(() => {
        if (updateSuccess) {
            setIsOpenSnackBar(true);
            fetchClearUpdateSurvey();
        }
    }, [isFetchingUpdate]);

    const handleSetParams = useCallback((field, valueChange) => {
        setParams((prevParams) => ({ ...prevParams, [field]: valueChange }));
    }, []);

    const validate = () => {
        const {
            survey_link_viewer, survey_link_organizer, survey_link_viewer_en, survey_link_organizer_en,
        } = params;
        const validateRules = {
            survey_link_viewer: [
                () => Validation.isValidUrl(survey_link_viewer, 500),
            ],
            survey_link_organizer: [
                () => Validation.isValidUrl(survey_link_organizer, 500),
            ],
            survey_link_viewer_en: [
                () => !_.isEmpty(eventInfo.languages) && Validation.isValidUrl(survey_link_viewer_en, 500),
            ],
            survey_link_organizer_en: [
                () => !_.isEmpty(eventInfo.languages) && Validation.isValidUrl(survey_link_organizer_en, 500),
            ],
        };
        const validateResult = Validation.validateAllRules(validateRules);

        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            window.scrollTo(0, 0);
            return false;
        }
        setValidateResults({});
        return true;
    };

    const hanleUpdateSurvey = () => {
        if (validate()) {
            let dataSubmit = {
                shortName: urlParams.shortName,
                data: {
                    survey_link_viewer: params.survey_link_viewer && (params.survey_link_viewer).trim(),
                    survey_link_organizer: params.survey_link_organizer && (params.survey_link_organizer).trim(),
                },
            };
            if (!_.isEmpty(eventInfo.languages)) {
                dataSubmit = {
                    ...dataSubmit,
                    data: {
                        ...dataSubmit.data,
                        survey_link_viewer_en: params.survey_link_viewer_en && (params.survey_link_viewer_en).trim(),
                        survey_link_organizer_en: params.survey_link_organizer_en && (params.survey_link_organizer_en).trim(),
                    },
                };
            }
            fetchUpdateSurvey(dataSubmit);
        }
    };

    return (
        <>
            <GridContainer style={{ justifyContent: 'center' }}>
                <GridItem md={10} className={classes.main}>
                    <fieldset className={classes.fieldset}>
                        <legend>
                            <Typography variant="h5" color="primary" bold>来場者へのアンケート</Typography>
                        </legend>
                        <GridContainer>
                            <GridItem md={2}>
                                アンケートリンク
                            </GridItem>
                            <GridItem md={10}>
                                <CleanableInput
                                    defaultValue={params.survey_link_viewer}
                                    onChange={(value) => handleSetParams('survey_link_viewer', value)}
                                    error={!!validateResults.survey_link_viewer}
                                    helperText={validateResults.survey_link_viewer
                                        ? intl.formatMessage({ id: validateResults.survey_link_viewer.message }, { maxLength: 1000 }) : ''}
                                />
                            </GridItem>
                        </GridContainer>
                        {!_.isEmpty(eventInfo.languages) && (
                            <GridContainer style={{ marginTop: '20px' }}>
                                <GridItem md={2}>
                                    アンケートリンク（英語）
                                </GridItem>
                                <GridItem md={10}>
                                    <CleanableInput
                                        defaultValue={params.survey_link_viewer_en}
                                        onChange={(value) => handleSetParams('survey_link_viewer_en', value)}
                                        error={!!validateResults.survey_link_viewer_en}
                                        helperText={validateResults.survey_link_viewer_en
                                            ? intl.formatMessage({ id: validateResults.survey_link_viewer_en.message }, { maxLength: 1000 }) : ''}
                                    />
                                </GridItem>
                            </GridContainer>
                        )}
                    </fieldset>
                </GridItem>
                <GridItem md={10} className={classes.main}>
                    <fieldset className={classes.fieldset}>
                        <legend>
                            <Typography variant="h5" color="primary" bold>出展者へのアンケート</Typography>
                        </legend>
                        <GridContainer>
                            <GridItem md={2}>
                                アンケートリンク
                            </GridItem>
                            <GridItem md={10}>
                                <CleanableInput
                                    defaultValue={params.survey_link_organizer}
                                    onChange={(value) => handleSetParams('survey_link_organizer', value)}
                                    error={!!validateResults.survey_link_organizer}
                                    helperText={validateResults.survey_link_organizer
                                        ? intl.formatMessage({ id: validateResults.survey_link_organizer.message }, { maxLength: 1000 }) : ''}
                                />
                            </GridItem>
                        </GridContainer>
                        {!_.isEmpty(eventInfo.languages) && (
                            <GridContainer style={{ marginTop: '20px' }}>
                                <GridItem md={2}>
                                    アンケートリンク（英語）
                                </GridItem>
                                <GridItem md={10}>
                                    <CleanableInput
                                        defaultValue={params.survey_link_organizer_en}
                                        onChange={(value) => handleSetParams('survey_link_organizer_en', value)}
                                        error={!!validateResults.survey_link_organizer_en}
                                        helperText={validateResults.survey_link_organizer_en
                                            ? intl.formatMessage({ id: validateResults.survey_link_organizer_en.message }, { maxLength: 1000 }) : ''}
                                    />
                                </GridItem>
                            </GridContainer>
                        )}
                    </fieldset>
                </GridItem>
                <GridItem md={10} style={{ textAlign: 'center', marginTop: 20 }}>
                    <Button
                        color="info"
                        style={{ width: '200px', fontSize: '20px' }}
                        size="md"
                        gradient
                        onClick={() => hanleUpdateSurvey()}
                    >
                        保存する
                    </Button>
                </GridItem>
            </GridContainer>

            <SnackBar
                open={isOpenSnackBar}
                onClose={() => setIsOpenSnackBar(false)}
                message="更新成功"
                severity="success"
                autoHideDuration={3000}
            />

            <Loader isLoading={isLoading} />
        </>
    );
};

SurveryManagement.defaultProps = {
    surveyData: {},
    isFetchingUpdate: false,
    updateSuccess: bool,
    eventInfo: {},
};

SurveryManagement.propTypes = {
    fetchClearGetSurvey: PropTypes.func.isRequired,
    fetchGetSurvey: PropTypes.func.isRequired,
    fetchClearUpdateSurvey: PropTypes.func.isRequired,
    fetchUpdateSurvey: PropTypes.func.isRequired,
    surveyData: PropTypes.oneOfType([() => null, PropTypes.objectOf]),
    isFetchingUpdate: PropTypes.bool,
    updateSuccess: PropTypes.bool,
    isLoading: PropTypes.bool.isRequired,
    eventInfo: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.bool,
        PropTypes.string,
        PropTypes.number,
    ])),
};

export default SurveryManagement;
